/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useMemo, useRef } from 'react';

import { css, jsx } from '@compiled/react';

import { ExperienceStart } from '@atlassian/experience-tracker';

import { ExperienceNames } from '../experience-tracker/ExperienceNames';
import { type useUrl } from './useUrl';
import { useIntl } from 'react-intl-next';
import { usePerformanceMetrics } from '../performance-metrics/performanceMetricsProvider';
import { useUniqueKey } from '../unique-key-provider/UniqueKeyProvider';
const iframeStyles = css({
	border: 'none',
	width: '100%',
	height: '100%',
});

type Props = Pick<Parameters<typeof useUrl>[0], 'contentId' | 'spaceKey' | 'parentProduct'> & {
	url?: string | null;
};

export const StorageAccessPage = ({
	contentId,
	spaceKey,
	url,
	parentProduct,
}: Props): JSX.Element => {
	const { locale } = useIntl();
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const { uniqueKey } = useUniqueKey();

	const { cancelIframeLoadMetrics } = usePerformanceMetrics();

	useEffect(() => {
		cancelIframeLoadMetrics();
	}, [cancelIframeLoadMetrics]);

	const src = useMemo(() => {
		const uniqueKeyParam = uniqueKey ? `&uniqueKey=${uniqueKey}` : '';
		const path = `/wiki/embed/storage-access?parentProduct=${parentProduct}&locale=${locale}${uniqueKeyParam}`;
		const origin = url ? new URL(url).origin : window.location.origin;
		return [origin, path].join('');
	}, [locale, parentProduct, url, uniqueKey]);

	return (
		<React.Fragment>
			<ExperienceStart name={ExperienceNames.STORAGE_ACCESS_PAGE} id={`${spaceKey}-${contentId}`} />
			<iframe
				data-testid="storage-access-page-iframe"
				css={iframeStyles}
				title="storage-access-iframe"
				src={src}
				ref={iframeRef}
			/>
		</React.Fragment>
	);
};
